import React, {useContext, useEffect, useRef, useState} from 'react';
import {Events} from '@src/@interfaces/services/Http/IAsyncUploader';
import {AsyncFileUploaderContext} from '@src/components/AsyncFileUploader/AsyncFileUploaderContext';
import {debounce} from '@src/helpers/debounce-throttle';
import LoadingIndicator from '@src/components/LoadingIndicator/LoadingIndicator';
import NowUploadedItemDetails from './NowUploadedItemDetails';
import {ISubscription} from '@src/@interfaces/services/Events/ISubscription';

export default function AsyncFilesUploadingProgressIndicator({mainControlsContainerRef: ref}: {
    mainControlsContainerRef: React.RefObject<HTMLDivElement>
}) {
    const {getItemsToLoad, listen, getProgress} = useContext(AsyncFileUploaderContext);
    const [itemsToLoadHashes, setItemsToLoadHashes] = useState<string[]>([]);
    const [progress, setProgress] = useState<number>(0);
    const [wrapperWidth, setWrapperWidth] = useState<number | null>(null);
    const [maxDetailsWidth, setMaxDetailsWidth] = useState<number | null>(null);
    const wrapperRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const resizeListener = debounce(() => {
            const wrapperWidth = wrapperRef.current?.offsetWidth ?? null;
            const controlsContainerWidth = ref.current?.offsetWidth ?? null;

            let maxDetailsWidth = null;
            if (wrapperWidth && controlsContainerWidth) {
                maxDetailsWidth = wrapperWidth - controlsContainerWidth - 40;
            }

            setWrapperWidth(wrapperWidth);
            setMaxDetailsWidth(maxDetailsWidth);
        }, 100);

        window.addEventListener('resize', resizeListener);

        resizeListener();

        return () => window.removeEventListener('resize', resizeListener);
    }, [ref, wrapperRef]);

    useEffect(() => {
        const subscriptions: ISubscription[] = [];

        const listener = () => {
            const hashes = getItemsToLoad().map(item => item.getMeta().uniqueId);

            setItemsToLoadHashes(hashes);
        };

        subscriptions.push(listen([
            Events.FILE_ADDED_TO_LIST,
            Events.FILE_REMOVED_FROM_LIST,
            Events.ERROR_OCCURRED,
        ], listener));

        subscriptions.push(listen(
                [Events.PROGRESS, Events.ABORT, Events.COMPLETE],
                () => setProgress(getProgress())
        ));

        listener();

        return () => subscriptions.forEach(item => item.unsubscribe());
    }, [getItemsToLoad, getProgress, listen]);

    return <div className="header__async-uploader-indicator__wrapper" ref={wrapperRef}>
        {itemsToLoadHashes.length ? (<>
            <div className="header__async-uploader-indicator__details" style={{width: `${maxDetailsWidth}px`}}>
                {getItemsToLoad().sort((a, b) => {
                    const [aMeta, bMeta] = [a, b].map(i => i.getMeta());
                    // now loading items should be first
                    if (aMeta.isLoading !== bMeta.isLoading) {
                        return +!aMeta.isLoading - +!bMeta.isLoading;
                    }

                    return aMeta.totalBytes - bMeta.totalBytes;
                }).slice(0, 3).map(item => {
                    const hash = item.getMeta().uniqueId;

                    return <NowUploadedItemDetails key={hash} hash={hash}/>;
                })}
            </div>

            <div className="header__async-uploader-indicator" style={{width: `${progress}%`}}>
                <LoadingIndicator
                        type="linear"
                        className="header__async-uploader-indicator__sliders-track"
                        style={{width: `${wrapperWidth}px`}}
                />
            </div>
        </>) : null}
    </div>;
}
